import { LEDGER_KEY, LEDGER_VERSION, LEGACY_FORECAST_KEY, LEGACY_SANDBOX_KEY, LEGACY_TRADE_KEYS, emptyLedger, normalize, } from './schema';
function parseArray(raw) {
    if (!raw)
        return [];
    try {
        const v = JSON.parse(raw);
        return Array.isArray(v) ? v : [];
    }
    catch {
        return [];
    }
}
// Read the ad-hoc ordoMentis_* keys and fold them into one v2 document. Legacy
// keys are left intact (non-destructive — safe rollback). Returns null when
// there's nothing to migrate.
export function migrateLegacy(store) {
    const state = emptyLedger();
    const migratedFrom = [];
    for (const key of Object.keys(LEGACY_TRADE_KEYS)) {
        const asset = LEGACY_TRADE_KEYS[key];
        const rows = parseArray(store.getItem(key));
        if (rows.length) {
            state.trades[asset].push(...rows);
            migratedFrom.push(key);
        }
    }
    const forecasts = parseArray(store.getItem(LEGACY_FORECAST_KEY));
    if (forecasts.length) {
        state.forecasts.push(...forecasts);
        migratedFrom.push(LEGACY_FORECAST_KEY);
    }
    const sandbox = parseArray(store.getItem(LEGACY_SANDBOX_KEY));
    if (sandbox.length) {
        state.sandbox.push(...sandbox);
        migratedFrom.push(LEGACY_SANDBOX_KEY);
    }
    if (!migratedFrom.length)
        return null;
    state.migratedFrom = migratedFrom;
    return state;
}
// Typed accessor over the unified ledger. On first construction it loads the v2
// document, or migrates the legacy keys forward (persisting the result), or
// starts empty.
export class LedgerStore {
    store;
    state;
    constructor(store) {
        this.store = store;
        this.state = this.load();
    }
    load() {
        const raw = this.store.getItem(LEDGER_KEY);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.version === LEDGER_VERSION)
                    return normalize(parsed);
            }
            catch {
                /* corrupt doc — fall through to migrate/empty */
            }
        }
        const migrated = migrateLegacy(this.store);
        if (migrated) {
            this.persist(migrated);
            return migrated;
        }
        return emptyLedger();
    }
    persist(state) {
        this.store.setItem(LEDGER_KEY, JSON.stringify(state));
    }
    save() {
        this.persist(this.state);
    }
    getTrades(asset) {
        return this.state.trades[asset].slice();
    }
    allTrades() {
        return [].concat(...Object.values(this.state.trades));
    }
    appendTrade(asset, trade) {
        this.state.trades[asset].push(trade);
        this.save();
    }
    getForecasts() {
        return this.state.forecasts.slice();
    }
    appendForecast(forecast) {
        this.state.forecasts.push(forecast);
        this.save();
    }
    getSandbox() {
        return this.state.sandbox.slice();
    }
    setSandbox(rows) {
        this.state.sandbox = rows.slice();
        this.save();
    }
    clearSandbox() {
        this.state.sandbox = [];
        this.save();
    }
    // Reset paper trades. With no asset, clears every bucket; with an asset, just that one.
    clearTrades(asset) {
        if (asset) {
            this.state.trades[asset] = [];
        }
        else {
            for (const a of Object.keys(this.state.trades))
                this.state.trades[a] = [];
        }
        this.save();
    }
    // Clear only Caesar's auto-paper trades (notes starting "auto paper"), leaving the
    // operator's manual trades intact. Mirrors the Caesar/Operator split in ledgerStats.
    clearCaesarTrades() {
        const isAuto = (t) => typeof t.notes === 'string' && /^auto paper/i.test(t.notes);
        for (const a of Object.keys(this.state.trades)) {
            this.state.trades[a] = this.state.trades[a].filter((t) => !isAuto(t));
        }
        this.save();
    }
    /** @deprecated Use clearCaesarTrades — kept as a thin back-compat alias for external callers. */
    clearTomasTrades() {
        this.clearCaesarTrades();
    }
    migratedFrom() {
        return (this.state.migratedFrom ?? []).slice();
    }
    snapshot() {
        return normalize(this.state);
    }
}
//# sourceMappingURL=store.js.map